import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { TrendType } from './trend-type';
import { NgClass } from '@angular/common';
import { MatIcon } from '@angular/material/icon';

@Component({
    selector: 'ca-trend-badge',
    templateUrl: './trend-badge.component.html',
    styleUrls: ['./trend-badge.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [NgClass, MatIcon]
})
export class TrendBadgeComponent {
    public value = input.required<number>();
    public previousValue = input.required<number>();
    /**
     * Inverts only badge sentiment (color), e.g. negative percentage can be shown as positive.
     * Arrow direction and +/- sign still follow the raw calculated percentage difference.
     * Use case: when lower values are better (e.g. costs or CO2 emissions), set this to true.
     * For example if costs go up by +20% you can show the trend badge as negative (red)
     */
    public inverted = input(false);

    protected difference = computed(() => this.getDifference(this.value(), this.previousValue()));
    protected showBadge = computed(() => this.difference() !== null);
    protected icon = computed(() => this.getIcon(this.difference()));
    protected trendType = computed(() => this.getTrendType(this.difference(), this.inverted()));
    protected displayedValue = computed(() => this.getDisplayedValue(this.difference()));

    private getDifference(value: number, previousValue: number): number | null {
        if (previousValue === 0) return null;

        return ((value - previousValue) / Math.abs(previousValue)) * 100;
    }

    private getIcon(difference: number | null): string {
        if (difference === null) return '';
        if (difference > 0) return 'arrow-up';
        if (difference < 0) return 'arrow-down';

        return 'arrow-right';
    }

    private getTrendType(difference: number | null, inverted: boolean): TrendType {
        if (difference === null || difference === 0) return 'neutral';
        if (!inverted) return difference > 0 ? 'positive' : 'negative';

        return difference > 0 ? 'negative' : 'positive';
    }

    private getDisplayedValue(difference: number | null): string {
        if (difference === null) return '';

        const roundedDifference = this.roundMathematically(difference);
        const displayedValue = Math.max(Math.min(roundedDifference, 999), -999);

        if (displayedValue > 0) return `+${displayedValue}`;

        return `${displayedValue}`;
    }

    private roundMathematically(value: number): number {
        // cover edge case of negative values
        // example: -0.5 should round to -1, not 0
        return Math.sign(value) * Math.round(Math.abs(value));
    }
}
